import axios from "../axios.js";

// WhatsApp (Evolution Go or Meta Cloud API, chosen on the server). The server composes the
// formatted caption (formal English, *bold*) from message = { kind, data }
// (see Util/whatsappTemplates.js) and attaches the PDF. Resolves to { to, messageId }.
// to: a number or a list of typed numbers; contactIds (with doc + docId): party
// contacts from getWhatsAppRecipients. One number → { to, messageId };
// several → { sent: [{ to, name }], failed: [{ to, name, error }] }.
export const sendWhatsAppPDF = ({ to, contactIds, doc, docId, message, pdfBlob, filename }) => {
    const form = new FormData();
    if (Array.isArray(to)) form.append("to", JSON.stringify(to));
    else if (to) form.append("to", to);
    if (contactIds?.length) {
        form.append("contact_ids", JSON.stringify(contactIds));
        form.append("doc", doc);
        form.append("docId", String(docId));
    }
    form.append("kind", message.kind);
    form.append("data", JSON.stringify(message.data || {}));
    form.append("filename", filename);
    form.append("pdf", pdfBlob, filename);
    return axios.post("/whatsapp/send", form, { headers: { "Content-Type": "multipart/form-data" } });
};

// Who gets a document on WhatsApp → { party: [contact], agent: [contact], masked }
// contact = { id, name, role, phone, scope: "party"|"address", address_label }
// doc: salesbill | saleschallan | salesorder | salesreturn | creditnote
export const getWhatsAppRecipients = (doc, id, config) =>
    axios.get("/whatsapp/recipients", { ...config, params: { doc, id } });

// ── Send Invoices page ──────────────────────────────────────────────────────
// The list itself is GET /whatsapp/invoice-queue through useServerList:
// rows = [{ invoiceid, billno, invoicedate, partyname, agentname, party_auto, agent_auto,
//           party_status, agent_status }], status = { state: sent|delivered|read|failed, at, by } | null;
// meta.autoInvoice = { enabled, source } (the 12 AM automatic job).
export const INVOICE_QUEUE_URL = "/whatsapp/invoice-queue";

// One bill, PDF rendered by the server → { results: [{ to, role, name, status: SENT|FAILED|SKIPPED, error }] }.
// alsoTo = typed numbers (go as the party's copy); force = resend to numbers that already have it.
export const sendInvoiceWhatsApp = (invoiceid, { partyContactIds = [], agentContactIds = [], alsoTo = [], force = false }) =>
    axios.post(`/whatsapp/send-invoice/${invoiceid}`, {
        party_contact_ids: partyContactIds, agent_contact_ids: agentContactIds, also_to: alsoTo, force,
    });

// ── Send Transport Receipts page ────────────────────────────────────────────
// GET /whatsapp/lr-queue through useServerList: bills with APPROVED receipts.
// rows = [{ invoiceid, billno, partyname, agentname, approved_at, lr_nos, photos, first_photo_id,
//           party_status, agent_status, lr_changed, skipped: { reason, by, at } | null }];
// meta.counts = { unsent, failed, sent, skipped, all }, meta.pipeline = { pending, attached },
// meta.autoLr = { enabled, source } (send on approval).
export const LR_QUEUE_URL = "/whatsapp/lr-queue";

// The bill's approved LR photos → picked numbers. Same body/response as sendInvoiceWhatsApp.
export const sendLrWhatsApp = (invoiceid, { partyContactIds = [], agentContactIds = [], alsoTo = [], force = false }) =>
    axios.post(`/whatsapp/send-lr/${invoiceid}`, {
        party_contact_ids: partyContactIds, agent_contact_ids: agentContactIds, also_to: alsoTo, force,
    });

// "Won't send" (reason required) and undo; bulk → { done: [ids], skipped: [{ id, message }] }.
export const skipLr = (invoiceid, reason) => axios.post(`/whatsapp/lr-skip/${invoiceid}`, { reason });
export const unskipLr = (invoiceid) => axios.delete(`/whatsapp/lr-skip/${invoiceid}`);
export const bulkSkipLr = (ids, reason) => axios.post("/whatsapp/lr-skip", { ids, reason });

// { configured, connected, name, testMode } — is the linked phone online?
export const getWhatsAppStatus = (config) => axios.get("/whatsapp/status", config);

// ── Inbox (Cloud API) ────────────────────────────────────────────────────────
// [{ num, partyname, profile_name, last_at, last_dir, last_text, unread, window_open }]
export const getConversations = (params, config) => axios.get("/whatsapp/conversations", { ...config, params });

// { number, partyname, profile_name, window_open, window_closes_at, has_more, messages: [...] }
export const getConversation = (number, params, config) =>
    axios.get(`/whatsapp/conversations/${encodeURIComponent(number)}`, { ...config, params });

// The full original WhatsApp payload one message was built from → { wamid, msg_type, body,
// received_at, raw }. Lets the Info panel show what a message that rendered as "[unsupported]"
// (or any "[type]" fallback) actually was.
export const getMessageRaw = (number, wamid, config) =>
    axios.get(`/whatsapp/conversations/${encodeURIComponent(number)}/messages/${encodeURIComponent(wamid)}/raw`, config);

export const markConversationRead = (number) => axios.post(`/whatsapp/conversations/${encodeURIComponent(number)}/read`);

// Free text, or a photo / file (`file`, with `text` as its caption). `replyTo` = the
// wamid being quoted. The server refuses (409) once the client's 24-hour window has closed.
export const sendWhatsAppReply = ({ to, text, replyTo, file }) => {
    if (!file) return axios.post("/whatsapp/reply", { to, text, ...(replyTo ? { replyTo } : {}) });
    const form = new FormData();
    form.append("to", to);
    if (text) form.append("text", text);
    if (replyTo) form.append("replyTo", replyTo);
    form.append("file", file, file.name);
    return axios.post("/whatsapp/reply", form, { headers: { "Content-Type": "multipart/form-data" } });
};

// An attachment's bytes as a Blob (`thumb` = the small JPEG preview). Blob bodies
// come back as the whole axios response, not unwrapped.
export const getWhatsAppMediaBlob = async (id, { thumb = false, signal } = {}) => {
    const res = await axios.get(`/whatsapp/media/${id}`, { params: thumb ? { thumb: 1 } : undefined, responseType: "blob", signal });
    return res?.data instanceof Blob ? res.data : new Blob([res?.data ?? res]);
};

// "typing…" on the client's phone for ~25 s (call at most every ~20 s).
export const sendWhatsAppTyping = (number) => axios.post(`/whatsapp/conversations/${encodeURIComponent(number)}/typing`);

// { total, chats } unread client messages — menu badge / tab title fallback.
export const getWhatsAppUnread = (config) => axios.get("/whatsapp/unread", config);

// Shared quick replies: [{ id, shortcut, title, body }]
export const getQuickReplies = (config) => axios.get("/whatsapp/quick-replies", config);
export const saveQuickReply = ({ id, shortcut, title, body }) =>
    (id ? axios.put(`/whatsapp/quick-replies/${id}`, { shortcut, title, body }) : axios.post("/whatsapp/quick-replies", { shortcut, title, body }));
export const deleteQuickReply = (id) => axios.delete(`/whatsapp/quick-replies/${id}`);

/**
 * Live inbox events (Server-Sent Events). EventSource can't send the Bearer header,
 * so this reads the stream with fetch. Resolves when the stream ends; rejects on
 * error (err.status = HTTP status). onEvent({ type, number, ... }) per event.
 */
export const streamWhatsAppEvents = async ({ token, signal, onEvent, onOpen }) => {
    const base = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const res = await fetch(`${base}/whatsapp/events`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
        credentials: "include",
        signal,
    });
    if (!res.ok || !res.body) throw Object.assign(new Error(`Live updates unavailable (${res.status})`), { status: res.status });
    onOpen?.();
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
        const { value, done } = await reader.read();
        if (done) return;
        buffer += decoder.decode(value, { stream: true });
        let cut;
        while ((cut = buffer.indexOf("\n\n")) >= 0) {
            const block = buffer.slice(0, cut);
            buffer = buffer.slice(cut + 2);
            const data = block.split("\n").filter((l) => l.startsWith("data:")).map((l) => l.slice(5).trim()).join("\n");
            if (!data) continue; // comments / pings
            try {
                onEvent(JSON.parse(data));
            } catch {
                // ignore a malformed event
            }
        }
    }
};

// Link a chat number to a party (partyid null = unlink). Needs party:view.
export const linkWhatsAppContact = (number, partyid) =>
    axios.put(`/whatsapp/conversations/${encodeURIComponent(number)}/contact`, { partyid: partyid ?? null });

// Photos / files exchanged in one chat (contact panel grid).
export const getChatMedia = (number, config) => axios.get(`/whatsapp/conversations/${encodeURIComponent(number)}/media`, config);

// A bill ({ kind: "invoice", invoiceid }) or statement ({ kind: "outstanding", firmid })
// as an approved template — works even after the 24-hour reply window.
export const sendWhatsAppDocument = (number, body) =>
    axios.post(`/whatsapp/conversations/${encodeURIComponent(number)}/template`, body);

// ── Team inbox ──────────────────────────────────────────────────────────────
// [{ id, name }] users who can work the inbox (whatsapp:send).
export const getInboxAgents = (config) => axios.get("/whatsapp/agents", config);
// userId null = unassign.
export const assignConversation = (number, userId) =>
    axios.put(`/whatsapp/conversations/${encodeURIComponent(number)}/assign`, { userId: userId ?? null });
// Pins are per user (max 5).
export const pinConversation = (number, pinned) =>
    axios.post(`/whatsapp/conversations/${encodeURIComponent(number)}/pin`, { pinned: Boolean(pinned) });
// "Mark as unread" (shared) — cleared when someone opens the chat.
export const markConversationUnread = (number) =>
    axios.post(`/whatsapp/conversations/${encodeURIComponent(number)}/unread`, { unread: true });
